import { Check, Minus } from 'lucide-react';
import { useEffect, useRef, type InputHTMLAttributes } from 'react';

/**
 * The one checkbox. A rounded square that never fills — ticking it draws the border and the
 * check in the brand yellow and leaves the box itself empty.
 *
 * **Every checkbox in the product is this component.** Before it there were three different
 * ticked colours in the codebase — `brand-500` in some places, `blue-500` in others, and a
 * rail-local constant — which meant the same control meant the same thing while looking like
 * three controls.
 *
 * The native input does the work and keeps the semantics: it stays in the DOM, focusable,
 * labellable, and announced as a checkbox. `appearance-none` only removes the platform
 * painting so the box can carry a radius and a colour the platform will not give it, and the
 * glyph is drawn over it with `peer-checked`. `accent-color` alone cannot do this — it fills
 * the box, which is the one thing this design does not do.
 *
 * Contrast, and it is load-bearing: with no fill, the **check itself** is the entire state
 * signal, so it has to clear WCAG 1.4.11's 3:1 on its own — there is no dark ground doing
 * that work for it.
 *
 * That is why the tick is `brand-700` (3.42:1) rather than `brand-500`. The brand yellow the
 * buttons use is 2.16:1 against white, and unlike a button — where the yellow is the *ground*
 * and the white label carries the meaning — here the yellow IS the meaning. At 2.16:1 a
 * ticked box is nearly indistinguishable from an empty one, which is a functional failure
 * rather than a cosmetic compromise. brand-700 is the brightest step on the brand ramp that
 * clears the bar; brand-800 (5.76:1) is the hover.
 *
 * The resting border is `slate-500` (4.76:1) rather than the lighter `slate-300` an unticked
 * box usually gets — an empty box's border is equally the only thing indicating the control,
 * and slate-300 is 1.5:1.
 */
export function Checkbox({
  indeterminate = false,
  className = '',
  ...props
}: {
  /**
   * The "some but not all" state, for a select-all. It is a property of the DOM node rather
   * than an attribute, so it can only be set through a ref — which is why every call site
   * used to carry the same `ref={(el) => ...}` line before this component existed.
   */
  indeterminate?: boolean;
} & InputHTMLAttributes<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <span className={`relative inline-flex shrink-0 ${className}`}>
      <input
        {...props}
        ref={ref}
        type="checkbox"
        className="
          peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-500 bg-transparent
          transition
          hover:border-slate-600
          checked:border-brand-700
          indeterminate:border-brand-700
          hover:checked:border-brand-800
          hover:indeterminate:border-brand-800
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-200
          disabled:cursor-not-allowed disabled:border-slate-300
        "
      />

      {/*
        Drawn over the box, never in place of it. `pointer-events-none` so the click always
        lands on the input beneath — a glyph that swallowed the click would make the control
        dead in exactly the state the user is trying to leave.

        Stroke 3 rather than the default 2: with nothing behind it, a hairline tick at 12px
        reads as grey rather than as the yellow it is.
      */}
      <Check
        aria-hidden="true"
        strokeWidth={3}
        className="
          pointer-events-none absolute inset-0 m-auto h-3 w-3 text-brand-700 opacity-0
          transition peer-checked:opacity-100 peer-indeterminate:opacity-0
          peer-hover:text-brand-800 peer-disabled:text-slate-400
        "
      />

      <Minus
        aria-hidden="true"
        strokeWidth={3}
        className="
          pointer-events-none absolute inset-0 m-auto h-3 w-3 text-brand-700 opacity-0
          transition peer-indeterminate:opacity-100
          peer-hover:text-brand-800 peer-disabled:text-slate-400
        "
      />
    </span>
  );
}
